import { useMemo, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api/client';
import type { ClientDetail, Video } from '@pixerito/api-client';
import { Dialog, Empty, ErrorNotice, Field, Icon, Loading } from '../../components/ui';
import { useAsync } from '../../lib/hooks';
import { currentPeriod, formatDate, formatInt, monthLong, monthName, shiftPeriod } from '../../lib/format';
import { parseInput } from '../../lib/parse';

const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Facebook', 'LinkedIn'];

export function AdminVideos({ client, onToast }: { client: ClientDetail; onToast: (m: string) => void }) {
  const [period, setPeriod] = useState(shiftPeriod(currentPeriod(), -1));
  const months = useMemo(() => Array.from({ length: 18 }, (_, i) => shiftPeriod(currentPeriod(), -i)), []);
  const { data, setData, error, loading, reload } = useAsync(() => api.videos(client.id, period), [client.id, period]);
  const [editing, setEditing] = useState<Video | 'new' | null>(null);

  const totals = (data ?? []).reduce(
    (t, v) => ({ views: t.views + v.views, likes: t.likes + v.likes, comments: t.comments + v.comments, shares: t.shares + v.shares }),
    { views: 0, likes: 0, comments: 0, shares: 0 },
  );

  return (
    <div className="stack-lg">
      <div className="entry-toolbar" style={{ marginBottom: 0, justifyContent: 'space-between' }}>
        <div className="field">
          <label htmlFor="video-month">Mês de publicação</label>
          <select id="video-month" className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {months.map((p) => (
              <option key={p} value={p}>
                {monthName(p)} {p.slice(0, 4)}
              </option>
            ))}
          </select>
        </div>
        <button className="btn btn--action btn--sm" onClick={() => setEditing('new')}>
          <Icon name="plus" size={15} />
          Adicionar vídeo
        </button>
      </div>

      {loading && !data ? (
        <Loading />
      ) : error ? (
        <ErrorNotice message={error} onRetry={reload} />
      ) : (
        data &&
        (data.length === 0 ? (
          <Empty title={`Nenhum vídeo em ${monthLong(period)}`}>
            Lance cada vídeo publicado no mês com os números da plataforma. Eles aparecem no painel do cliente comparados com o mês
            anterior.
          </Empty>
        ) : (
          <div className="card card--flush">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Vídeo</th>
                    <th className="r">Views</th>
                    <th className="r">Curtidas</th>
                    <th className="r">Coment.</th>
                    <th className="r">Compart.</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <div className="stack" style={{ gap: 0 }}>
                          <span style={{ fontWeight: 500 }}>{v.title}</span>
                          <span className="t-caption">
                            {v.platform}
                            {v.publishedOn ? ` · ${formatDate(v.publishedOn)}` : ''}
                          </span>
                        </div>
                      </td>
                      <td className="r num">{formatInt(v.views)}</td>
                      <td className="r num">{formatInt(v.likes)}</td>
                      <td className="r num">{formatInt(v.comments)}</td>
                      <td className="r num">{formatInt(v.shares)}</td>
                      <td className="r">
                        <div className="row" style={{ gap: 4, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <button className="icon-btn" aria-label={`Editar ${v.title}`} onClick={() => setEditing(v)}>
                            <Icon name="edit" />
                          </button>
                          <button
                            className="icon-btn"
                            aria-label={`Excluir ${v.title}`}
                            onClick={async () => {
                              await api.deleteVideo(v.id);
                              setData((list) => (list ?? []).filter((x) => x.id !== v.id));
                              onToast('Vídeo excluído');
                            }}
                          >
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="label">
                      Total · {data.length} vídeo{data.length === 1 ? '' : 's'}
                    </td>
                    <td className="r num" style={{ fontWeight: 600 }}>
                      {formatInt(totals.views)}
                    </td>
                    <td className="r num" style={{ fontWeight: 600 }}>
                      {formatInt(totals.likes)}
                    </td>
                    <td className="r num" style={{ fontWeight: 600 }}>
                      {formatInt(totals.comments)}
                    </td>
                    <td className="r num" style={{ fontWeight: 600 }}>
                      {formatInt(totals.shares)}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {editing && (
        <VideoDialog
          clientId={client.id}
          period={period}
          video={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={(v, created) => {
            setEditing(null);
            if (v.period !== period) {
              setPeriod(v.period);
            } else setData((list) => (created ? [...(list ?? []), v] : (list ?? []).map((x) => (x.id === v.id ? v : x))));
            onToast(created ? 'Vídeo adicionado' : 'Vídeo salvo');
          }}
        />
      )}
    </div>
  );
}

function VideoDialog({
  clientId,
  period,
  video,
  onClose,
  onSaved,
}: {
  clientId: string;
  period: string;
  video?: Video;
  onClose: () => void;
  onSaved: (v: Video, created: boolean) => void;
}) {
  const [form, setForm] = useState({
    title: video?.title ?? '',
    platform: video?.platform ?? 'Instagram',
    url: video?.url ?? '',
    publishedOn: video?.publishedOn ?? '',
    views: video ? String(video.views) : '',
    likes: video ? String(video.likes) : '',
    comments: video ? String(video.comments) : '',
    shares: video ? String(video.shares) : '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    const local: Record<string, string> = {};
    const nums: Record<string, number> = {};
    for (const k of ['views', 'likes', 'comments', 'shares'] as const) {
      const v = parseInput(form[k], 0);
      if (v === 'invalid' || (typeof v === 'number' && v < 0)) local[k] = 'Número inválido.';
      else nums[k] = v ?? 0;
    }
    if (!form.title.trim()) local.title = 'Informe o título do vídeo.';
    setErrors(local);
    if (Object.keys(local).length) return;

    const publishedPeriod = form.publishedOn ? form.publishedOn.slice(0, 7) : (video?.period ?? period);
    const body = {
      period: publishedPeriod,
      title: form.title,
      platform: form.platform,
      url: form.url || null,
      publishedOn: form.publishedOn || null,
      ...nums,
    };
    setBusy(true);
    try {
      onSaved(video ? await api.updateVideo(video.id, body) : await api.createVideo(clientId, body), !video);
    } catch (err) {
      if (err instanceof ApiError) setErrors({ ...err.fields, _: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={video ? 'Editar vídeo' : `Vídeo de ${monthLong(period)}`} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        {errors._ && <div className="notice notice--error">{errors._}</div>}
        <Field label="Título" htmlFor="vd-title" error={errors.title}>
          <input id="vd-title" className="input" value={form.title} onChange={set('title')} autoFocus />
        </Field>
        <div className="grid-2">
          <Field label="Plataforma" htmlFor="vd-platform">
            <select id="vd-platform" className="select" value={form.platform} onChange={set('platform')}>
              {PLATFORMS.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </Field>
          <Field label="Publicado em" htmlFor="vd-date" hint="Define o mês do vídeo.">
            <input id="vd-date" className="input" type="date" value={form.publishedOn} onChange={set('publishedOn')} />
          </Field>
        </div>
        <Field label="Link" htmlFor="vd-url" error={errors.url} hint="Opcional.">
          <input
            id="vd-url"
            className="input"
            type="url"
            value={form.url}
            onChange={set('url')}
            placeholder="https://www.instagram.com/reel/…"
          />
        </Field>
        <div className="grid-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          {(
            [
              ['views', 'Visualizações'],
              ['likes', 'Curtidas'],
              ['comments', 'Comentários'],
              ['shares', 'Compartilhamentos'],
            ] as const
          ).map(([k, label]) => (
            <Field key={k} label={label} htmlFor={`vd-${k}`} error={errors[k]}>
              <input id={`vd-${k}`} className="input input--num" inputMode="numeric" value={form[k]} onChange={set(k)} placeholder="0" />
            </Field>
          ))}
        </div>
        <div className="row">
          <button className="btn btn--action" type="submit" disabled={busy}>
            {busy ? 'Salvando…' : video ? 'Salvar' : 'Adicionar vídeo'}
          </button>
          <button className="btn btn--quiet" type="button" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </form>
    </Dialog>
  );
}
